import { ChangeDetectorRef, Injectable } from '@angular/core';
import { act, Actions, createEffect, ofType } from '@ngrx/effects';
import {
  map,
  catchError,
  tap,
  mergeMap,
  withLatestFrom,
  exhaustMap,
} from 'rxjs/operators';
import { of } from 'rxjs';
import * as fromAuthActions from '../actions/auth.actions';
import * as fromAccountActions from '../actions/account.actions';
import { Store } from '@ngrx/store';
import { AppState } from '../app.state';
import { AuthService } from 'src/app/services/auth.service';
import { setLoadingSpinner } from '../actions/shared.actions';
import { ErrorService } from 'src/app/services/error.service';
import { getUserProfile } from '../actions/account.actions';
import { isPlatform, ModalController, NavController, ToastController } from '@ionic/angular';
import { OfflineStorageService } from 'src/app/services/offline-storage.service';
import { NavigationExtras, Router } from '@angular/router';
import { getFCMToken } from '../selectors/shared.selector';
import { StorageKeys } from 'src/app/utils/storage-keys';
import { BiometricService } from 'src/app/services/biometric.service';
import { PushService } from 'src/app/services/push.service';
import { ErrorComponent } from 'src/app/shared/modals/error/error.component';
import { SuccessComponent } from 'src/app/shared/modals/success/success.component';

@Injectable()
export class AuthEffects {

  logIn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuthActions.login),
      exhaustMap((action) =>
        this.authService.login(action?.data).pipe(
          map((response: any) => {
            console.log('logIn$', action);
            this.store.dispatch(setLoadingSpinner({ status: false }));
            const user = this.authService.formatUser(response?.details);
            this.authService.setUserSession(user);
            return fromAuthActions.logInSuccess({ data: user, redirect: true });
          }),
          catchError((response) => {
            console.error('logIn$', response);
            this.store.dispatch(setLoadingSpinner({ status: false }));
            const error = {
              status: response.status,
              error: response.message
            };
            return of(fromAuthActions.loginFailed({ data: error, payload: action }));
          })
        )
      )
    )
  );

  loginRedirect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAuthActions.logInSuccess),
        tap(async (action) => {
          console.log('loginRedirect$', action);
          if (action.redirect) {
            this.store.dispatch(getUserProfile());
          }
          // Also runs on every app start via autoLogin, which keeps the token fresh.
          if (action.data?.token) {
            this.pushService.register(action.data.token);
          }
        })
      ),
    { dispatch: false }
  );

  loginFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAuthActions.loginFailed),
        tap(async (action) => {
          const message = {
            title: 'Something went wrong',
            message: 'Invalid Credentials'
          }
          const modal = await this.modalController.create({
            component: ErrorComponent,
            cssClass: 'error-modal',
            componentProps: {
              message
            }
          });

          return modal.present();
        })
      ),
    { dispatch: false }
  );

  autoLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuthActions.autoLogin),
      withLatestFrom(this.store.select(getFCMToken), (action, fcmToken) => {
        return { ...action, device_token: fcmToken };
      }),
      mergeMap(async (action) => {
        const user = await this.authService.getUserSession();
        console.log('autoLogin$', user)
        return fromAuthActions.logInSuccess({ data: user, redirect: user ? true : false });
      })
    )
  );

  forgotPassword$ = createEffect(
    () => this.actions$.pipe(
      ofType(fromAuthActions.forgotPassword),
      exhaustMap((action) =>
        this.authService.forgotPassword(action.data).pipe(
          map((response) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            return fromAuthActions.forgotSuccess({ data: response?.message })
          }),
          catchError((response) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            return of(fromAuthActions.forgotFailed({ data: response?.error?.message }))
          })
        )
      )
    )
  )

  forgotPasswordSuccess$ = createEffect(
    () => this.actions$.pipe(
      ofType(fromAuthActions.forgotSuccess),
      tap(async (action) => {
        const message = {
          message: `If your account exists, a password reset link has been sent to the associated email.`
        }
        const modal = await this.modalController.create({
          component: SuccessComponent,
          cssClass: 'success-modal',
          componentProps: {
            message
          }
        });

        modal.onDidDismiss()
          .then(() => {
              this.router.navigateByUrl('/auth/sign-in', { replaceUrl: true });
          });

        await modal.present();        

      })
    ),
    { dispatch: false }
  )

  forgotPasswordFailed$ = createEffect(
    () => this.actions$.pipe(
      ofType(fromAuthActions.forgotFailed),
      tap(async (action) => {
        let message = ''
        if (action.data) {
          message = action?.data
        } else {
          message = 'Unkown error ocurred. Please try again later.'
        }

        const modal = await this.modalController.create({
          component: ErrorComponent,
          cssClass: 'error-modal',
          componentProps: {
            message
          }
        });
        return modal.present();
      })
    ),
    { dispatch: false }
  )

  logOut$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAuthActions.autoLogout),
        tap(async () => {
          await this.pushService.unregister();
          this.authService.logout();
          this.navCtrl.navigateRoot('/');
        })
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private authService: AuthService,
    private navCtrl: NavController,
    private store: Store<AppState>,
    private errorService: ErrorService,
    private modalController: ModalController,
    private storage: OfflineStorageService,
    private toastController: ToastController,
    private offlineStorageService: OfflineStorageService,
    private storageKeys: StorageKeys,
    private biometricService: BiometricService,
    private router: Router,
    private pushService: PushService
  ) { }
}
